import { RootState } from '@iwProvider/configureStore';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { InjectedFormProps, reduxForm } from 'redux-form';

import AddressDetails from './addressDetails/AddressDetails';
import ContactDetails from './contactDetails/ContactDetails';
import PersonDetails from './personDetails/PersonDetails';
import { DELETE_PERSON, PERSON_FORM_SUBMIT } from './PersonModal.actions';
import styles from './PersonModal.module.css';
import { validate } from './PersonModalValidate';
import {
    Person as TDeserialisedPerson,
    getPersonFromUID,
    isDirector,
    isShareholder
} from '../../../api/lendingAPI/CustomerStateAPIHelper';
import { FormattedName } from '../../../iwIntlProvider/FormattedName';
import { CLOSE_MODAL } from '../../../iwModals/Modals.actions';
import ConfirmationButton from '../../ui/confirmationButton/ConfirmationButton';
import { CancelAction } from '../../ui/confirmationResponse/confirmationQuestion/cancelAction/CancelAction';
import { ConfirmAction } from '../../ui/confirmationResponse/confirmationQuestion/confirmAction/ConfirmAction';
import { ConfirmationQuestion } from '../../ui/confirmationResponse/confirmationQuestion/ConfirmationQuestion';
import { ConfirmationResponse } from '../../ui/confirmationResponse/ConfirmationResponse';
import { ConfirmationStatement } from '../../ui/confirmationResponse/confirmationStatement/ConfirmationStatement';
import { Prompt } from '../../ui/prompt';

const formConfiguration = {
    form: 'person',
    validate: (
        values: TDeserialisedPerson & { ready: boolean },
        props: PropsFromRedux
    ) => props.personFormValidate(values)
};

const mapStateToProps = ({
    api,
    personModal
}: {
    api: RootState['api'];
    personModal: RootState['personModal'];
}) => {
    const person = getPersonFromUID(api.customer_state.data, personModal.uid!);
    return {
        ...personModal,
        initialValues: {
            ...personModal,
            isShareholder: isShareholder(person),
            isDirector: isDirector(person)
        },
        personFormValidate: (values: any) =>
            validate(api.customer_state.data.application.people, values)
    };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    closeModal: () => dispatch({ type: CLOSE_MODAL, id: 'PersonModal' }),
    submitPersonForm: (person: TDeserialisedPerson) =>
        dispatch({ type: PERSON_FORM_SUBMIT, person }),
    deletePerson: (uid: string) => dispatch({ type: DELETE_PERSON, uid })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;
const PersonModal = ({
    submitPersonForm,
    deletePerson,
    uid,
    isEditingPerson,
    firstName,
    lastName,
    isApplicant,
    isGuarantor,
    handleSubmit,
    personFormSubmitting,
    closeModal
}: PropsFromRedux & InjectedFormProps) => {
    const handleSubmitPersonForm = (person: TDeserialisedPerson) => {
        submitPersonForm(person);
    };

    const handleDeletePerson = () => {
        deletePerson(uid!);
    };

    const renderTitle = () => {
        if (!isEditingPerson) {
            return (
                <FormattedMessage
                    id="_.person.title"
                    defaultMessage="Add a person"
                />
            );
        } else {
            const name = (
                <FormattedName
                    firstName={firstName}
                    lastName={lastName}
                    possessive={true}
                />
            );

            return (
                <FormattedMessage
                    id="_.edit_person.title"
                    defaultMessage="Edit {name} details"
                    values={{ name }}
                />
            );
        }
    };

    const renderDeletePerson = () => {
        const name = (
            <FormattedName firstName={firstName} lastName={lastName} />
        );

        if (isApplicant || isGuarantor || !isEditingPerson) {
            return null;
        }

        return (
            <ConfirmationResponse
                className={styles.deletePerson}
                onSubmit={handleDeletePerson}
            >
                <ConfirmationStatement>
                    <FormattedMessage
                        id="_.person.delete_person"
                        defaultMessage="Remove {name}"
                        values={{ name }}
                    />
                </ConfirmationStatement>

                <ConfirmationQuestion>
                    <FormattedMessage
                        id="_.person.delete_confirmation"
                        defaultMessage="Are you sure?"
                    />

                    <ConfirmAction>
                        <FormattedMessage
                            id="_.person.confirm_delete"
                            defaultMessage="Yes"
                        />
                    </ConfirmAction>
                    <CancelAction>
                        <FormattedMessage
                            id="_.person.cancel_delete"
                            defaultMessage="No"
                        />
                    </CancelAction>
                </ConfirmationQuestion>
            </ConfirmationResponse>
        );
    };

    return (
        <div className={styles.PersonModal}>
            <div className={styles.title} data-testid="title">
                {renderTitle()}
                {renderDeletePerson()}
            </div>

            <form
                // @ts-expect-error Redux form being awkward. Can be properly typed when Redux Form is removed.
                onSubmit={handleSubmit(handleSubmitPersonForm)}
                autoComplete="off"
                data-testid="person-form"
            >
                <PersonDetails />

                <ContactDetails />

                <AddressDetails />

                <Prompt className={styles.prompt}>
                    <button onClick={closeModal}>
                        <FormattedMessage
                            id="_.your_company.prompt.cancel"
                            defaultMessage="Cancel"
                        />
                    </button>
                    <ConfirmationButton
                        loading={personFormSubmitting}
                        type="submit"
                    >
                        <FormattedMessage
                            id="_.your_company.prompt.save"
                            defaultMessage="Save"
                        />
                    </ConfirmationButton>
                </Prompt>
            </form>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(reduxForm<object, any>(formConfiguration)(PersonModal));
